import { useNavigate } from "react-router-dom";
import { ArrowRight, ShieldCheck, Users, FileText, Search, Lock, Globe, MapPin } from "lucide-react";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/Button";
import { Helmet } from "react-helmet-async";

export function Servicos() {
  const navigate = useNavigate();

  const handleNavigateToPlanos = () => {
    navigate("/planos");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const servicosDetalhados = [
    {
      icon: FileText,
      title: "Implementação e Revisão de Políticas e Procedimentos",
      description: "Desenvolvimento e revisão de políticas, procedimentos e códigos de ética. Alinhamento às melhores práticas e regulamentações vigentes. Promoção de um ambiente organizacional íntegro e seguro."
    },
    {
      icon: ShieldCheck,
      title: "Canal de Denúncias",
      description: "Criação de canais formais e seguros, com acesso confidencial e sigiloso. Tratamento eficaz das denúncias, com proteção ao denunciante."
    },
    {
      icon: Search,
      title: "Investigação",
      description: "Investigações internas e externas com imparcialidade e sigilo. Conformidade com normas éticas e legais, garantindo transparência nos processos."
    },
    {
      icon: Users,
      title: "Due Diligence em Terceiros",
      description: "Análise de riscos e avaliação de fornecedores e parceiros comerciais. Garantia de conformidade e integridade na cadeia de negócios."
    },
    {
      icon: Lock,
      title: "Políticas de LGPD",
      description: "Criação de políticas personalizadas para atender à LGPD. Definição de diretrizes e normas internas de proteção de dados. Fortalecimento da governança corporativa."
    },
    {
      icon: Globe,
      title: "Gestão de Certidões Empresariais",
      description: "Monitoramento, renovação e controle de certidões municipais, estaduais e federais. Geração de relatórios e alertas automáticos para evitar interrupções."
    }
  ];

  const regioesAtuacao = [
    "Campinas (SP)",
    "Uberlândia (MG)",
    "Vila Velha (ES)",
    "Niterói (RJ)",
    "São José dos Campos (SP)",
    "Juiz de Fora (MG)"
  ];

  return (
    <>
      <Helmet>
        <title>Serviços - Portal Ethics</title>
        <meta name="description" content="Conheça nossos serviços de compliance, governança corporativa e programa de integridade." />
      </Helmet>
      
      <div className="min-h-screen bg-background-light">
        <Header />
        
        <main className="pt-20">
          {/* Hero Section */}
          <section className="relative h-[640px] flex items-center overflow-hidden">
            <div className="absolute inset-0 z-0">
              <div className="absolute inset-0 bg-gradient-to-r from-neutral-900/80 to-transparent z-10" />
              <img
                src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=1920&q=80"
                alt="Corporate services"
                className="object-cover w-full h-full"
              />
            </div>
            
            <div className="relative z-20 max-w-7xl mx-auto px-6 w-full">
              <div className="max-w-2xl">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary-blue/20 text-white rounded-full mb-6 border border-white/10 backdrop-blur-sm">
                  <ShieldCheck className="w-4 h-4" />
                  <span className="text-xs font-bold tracking-wider uppercase">Serviços Especializados</span>
                </div>
                
                <h1 className="text-5xl md:text-7xl font-black text-white leading-tight mb-6">
                  Compliance e <br />
                  <span className="italic text-blue-200">Governança Corporativa</span>
                </h1>
                
                <p className="text-lg text-white/80 mb-10 leading-relaxed font-light">
                  Soluções completas em ética, compliance e governança corporativa para transformar sua cultura organizacional com transparência e segurança.
                </p>
                
                <div className="flex flex-wrap gap-4">
                  <a href="http://wa.me/5511988890360" target="_blank" rel="noopener noreferrer">
                    <Button size="lg" className="group">
                      Falar com Especialista
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </a>
                  <Button 
                    variant="outline" 
                    size="lg" 
                    className="border-white/30 text-white hover:bg-white/20 bg-white/10 backdrop-blur-md"
                    onClick={handleNavigateToPlanos}
                  >
                    Ver Planos
                  </Button>
                </div>
              </div>
            </div>
          </section>

          

          {/* Programa de Integridade Section */}
          <section className="py-24 bg-background-light">
            <div className="max-w-7xl mx-auto px-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary-blue/10 text-primary-blue rounded-full mb-6">
                    <ShieldCheck className="w-4 h-4" />
                    <span className="text-xs font-bold tracking-wider uppercase">Programa de Integridade</span>
                  </div>
                  <h2 className="text-4xl font-black text-neutral-900 leading-tight mb-6">
                    Programa de Integridade e Compliance Empresarial: Ética e Segurança nas Operações
                  </h2>
                  <div className="bg-white rounded-2xl p-16 shadow-lg">
                    <h3 className="text-xl font-bold text-neutral-900 mb-4">Objetivo do Serviço</h3>
                    <p className="text-neutral-600 leading-relaxed mb-4">
                      O objetivo da nossa consultoria é implementar e gerenciar um Programa de Integridade que promova a ética corporativa, prevenindo fraudes e assegurando a transparência nas operações.
                    </p>
                    <p className="text-neutral-600 leading-relaxed mb-4">
                      Focamos na segurança dos processos e no fortalecimento da cultura organizacional, com práticas e políticas alinhadas às legislações vigentes.
                    </p>
                    <p className="text-neutral-600 leading-relaxed">
                      Criamos um ambiente íntegro e conforme às leis e normas regulatórias, atendendo empresas de médio e grande porte em regiões como Campinas (SP), Uberlândia (MG), Vila Velha (ES) e Niterói (RJ).
                    </p>
                  </div>
                </div>
                
              </div>
            </div>
          </section>

          {/* Serviços Detalhados */}
          <section className="py-24 bg-white">
            <div className="max-w-7xl mx-auto px-6">
              <div className="text-center max-w-3xl mx-auto mb-16">
                <h2 className="text-4xl font-black text-neutral-900 leading-tight mb-6">
                  O Que Oferecemos
                </h2>
                <p className="text-neutral-600 leading-relaxed">
                  Conheça em detalhes cada um dos nossos serviços especializados em compliance e governança corporativa.
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {servicosDetalhados.map((servico, index) => {
                  const Icon = servico.icon;
                  return (
                    <div key={index} className="group bg-background-light rounded-2xl p-8 border border-transparent hover:border-primary-blue/20 hover:bg-white hover:shadow-xl transition-all">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 bg-primary-blue/10 text-primary-blue rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-primary-blue group-hover:text-white transition-colors">
                          <Icon className="w-7 h-7" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-neutral-900 mb-3 group-hover:text-primary-blue transition-colors">
                            {servico.title}
                          </h3>
                          <p className="text-neutral-600 leading-relaxed">
                            {servico.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Regiões de Atuação */}
          <section className="py-24 bg-background-light">
            <div className="max-w-7xl mx-auto px-6">
              <div className="bg-primary-blue rounded-3xl p-8 md:p-12">
                <div className="text-center mb-8">
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/20 text-white rounded-full mb-6">
                    <MapPin className="w-4 h-4" />
                    <span className="text-xs font-bold tracking-wider uppercase">Regiões de Atuação</span>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-black text-white mb-4">
                    Onde Estamos Presentes
                  </h2>
                  <p className="text-white/80 max-w-2xl mx-auto">
                    A ETHICS, COMPLIANCE & SOLUTIONS atua em diversas cidades com forte presença empresarial e industrial, valorizando a integridade corporativa como vantagem competitiva.
                  </p>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  {regioesAtuacao.map((regiao, index) => (
                    <div key={index} className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-xl p-4">
                      <MapPin className="w-5 h-5 text-white flex-shrink-0" />
                      <span className="text-white text-sm font-medium">{regiao}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-20 bg-white relative overflow-hidden">
            <div className="max-w-4xl mx-auto px-6 text-center">
              <h2 className="text-3xl md:text-5xl font-black text-neutral-900 mb-6">
                Pronto para Implementar seu Programa de Integridade?
              </h2>
              <p className="text-neutral-600 text-lg mb-10 max-w-2xl mx-auto">
                Entre em contato com nossa equipe de especialistas para encontrar a solução ideal para sua empresa.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="http://wa.me/5511988890360" target="_blank" rel="noopener noreferrer">
                  <Button size="lg" className="group">
                    Falar com Especialista
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </a>
                <Button 
                  variant="outline" 
                  size="lg" 
                  onClick={handleNavigateToPlanos}
                >
                  Ver Planos e Preços
                </Button>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}